/**
 * TelemetryDisplay — the bottom bar: the BLITZ button, the back chevron, the "+" and, behind
 * ?dev=1, the live mechanical stats.
 *
 * The button matters more than it looks. A swipe is the natural way to advance a feed, but
 * interactive content owns the touch surface it sits on, and a web card in a sandboxed iframe
 * swallows pointer events entirely — the gesture never reaches this document, so the feed
 * cannot see it. A control in the PARENT dom always works, whatever the card is doing, which
 * makes it the one navigation affordance that can never be captured by content.
 *
 * It never dims: after the last card comes the end card, and BLITZ there starts over.
 *
 * The chevron beside it is the way back, and the only one. It lives in this same parent DOM
 * for the same reason; browser history stays one entry (share.ts), so hardware Back and the
 * iOS edge-swipe leave the site rather than move the feed. It is dimmed at the first card,
 * where there is nothing behind, and from the end card it returns to the last card.
 *
 * Stats are fed at 4Hz by the worker's TelemetryAgent (LiveStats), not by the shipped batches.
 * They are the builder's numbers, not the visitor's, so they render only with ?dev=1 — the
 * columns either side of BLITZ stay, so it sits dead centre with or without them.
 */
import { Show } from 'solid-js';
import type { LiveStats } from '../telemetry/TelemetryTypes';

export interface TelemetryDisplayProps {
  stats: LiveStats | null;
  inputMode: 'sab' | 'postMessage' | null;
  /** Advance to the next card (or, on the end card, back to the top). */
  onNext: () => void;
  /** Return to the previous card; from the end card, to the last card. */
  onBack: () => void;
  /** False at the first card: the chevron is shown but dimmed and disabled. */
  canBack: boolean;
  /** Open the "link a website / upload" sheet. */
  onLink: () => void;
  /** ?dev=1: show the stat cells. */
  dev: boolean;
}

function mmss(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function TelemetryDisplay(props: TelemetryDisplayProps) {
  const s = () => props.stats;
  return (
    <footer class="bottom-bar" data-ui aria-label="Navigation">
      {/* Equal columns either side, so BLITZ sits dead centre whatever the stats measure. */}
      <div class="stats left">
        <Show when={props.dev}>
          <div class="stat">
            <span class="value" data-testid="stat-session">{s() ? mmss(s()!.sessionMs) : '0:00'}</span>
            <span class="label">session</span>
          </div>
          <div class="stat accent">
            <span class="value" data-testid="stat-apm">{s() ? Math.round(s()!.apm) : '—'}</span>
            <span class="label">apm</span>
          </div>
          <div class="stat">
            <span class="value" data-testid="stat-decision">
              <Show when={s() && s()!.decisionSamples > 0} fallback="—">
                {Math.round(s()!.decisionLatencyMs)}
                <small>ms</small>
              </Show>
            </span>
            <span class="label">decision</span>
          </div>
        </Show>
        {/* At the right end of the column, next to BLITZ: a 36 px chevron in a 48 px hit area.
            The grid keeps the two controls in separate cells, so their hit areas never overlap. */}
        <button
          type="button"
          class="blitz-back"
          data-testid="blitz-back"
          onClick={() => props.onBack()}
          disabled={!props.canBack}
          aria-label="Previous card"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M15 6l-6 6 6 6" />
          </svg>
        </button>
      </div>

      <button type="button" class="blitz-next" data-testid="blitz-next" onClick={() => props.onNext()} aria-label="Next card">
        <span class="word">BLITZ</span>
        <span class="chev" aria-hidden="true">&uarr;</span>
      </button>

      <div class="stats right">
        <Show when={props.dev}>
          <div class="stat">
            <span class="value" data-testid="stat-fps">{s() ? Math.round(s()!.fps) : '—'}</span>
            <span class="label">fps</span>
          </div>
          <div class="stat" title={props.inputMode === 'sab' ? 'SharedArrayBuffer zero-copy input' : 'postMessage input fallback'}>
            <span class="value" data-testid="stat-input">
              <Show when={s() && s()!.inputEvents > 0} fallback="—">
                {s()!.pipelineLatencyMs < 10 ? s()!.pipelineLatencyMs.toFixed(1) : Math.round(s()!.pipelineLatencyMs)}
                <small>ms</small>
              </Show>
            </span>
            <span class="label">{props.inputMode === 'postMessage' ? 'input·pm' : props.inputMode === 'sab' ? 'input·sab' : 'input'}</span>
          </div>
        </Show>
        <button
          type="button"
          class="link-site"
          data-testid="link-site"
          onClick={() => props.onLink()}
          aria-label="Add something: link a website or upload"
          title="Link a website or upload"
        >
          +
        </button>
      </div>
    </footer>
  );
}
